/**
 * Shared field primitives for the InfoTrack-style submission forms.
 *
 * The full SDLT5 / AP1 / MR01 forms each have ~30-60 fields organised
 * into 6-9 collapsible panels. Rather than re-declaring `<Label>` +
 * `<Input>` + error/hint markup 200 times, we centralise the JSX here.
 *
 * All primitives accept the same shape:
 *   { name, label, hint, required, error, value, onChange, ... }
 *
 * `data-testid` is auto-derived as `field-${name}` so the QA agent has
 * a deterministic hook on every input.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

function FieldShell({ name, label, hint, required, error, children, span = 1 }) {
  const spanCls = span === 2 ? "sm:col-span-2" : span === 3 ? "sm:col-span-3" : "";
  return (
    <div className={`space-y-1 ${spanCls}`}>
      <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
        {label}
        {required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      {children}
      {error ? (
        <p className="text-[11px] text-rose-600">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

export function TextField({ name, label, hint, required, error, value, onChange, placeholder, type = "text", step, span }) {
  return (
    <FieldShell name={name} label={label} hint={hint} required={required} error={error} span={span}>
      <Input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 text-sm"
        data-testid={`field-${name}`}
      />
    </FieldShell>
  );
}

export function NumberField(props) {
  return <TextField {...props} type="number" step={props.step || "0.01"} />;
}

export function DateField(props) {
  return <TextField {...props} type="date" />;
}

export function TextAreaField({ name, label, hint, required, error, value, onChange, rows = 2, span = 3 }) {
  return (
    <FieldShell name={name} label={label} hint={hint} required={required} error={error} span={span}>
      <Textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm"
        data-testid={`field-${name}`}
      />
    </FieldShell>
  );
}

export function SelectField({ name, label, hint, required, error, value, onChange, options, placeholder = "Choose…", span }) {
  // Radix UI <Select.Item /> forbids empty-string values (it uses ''
  // internally for "unset"). Defensive: coerce empty -> undefined so
  // the Select shows the placeholder rather than crashing the tree.
  const safeValue = value === "" || value == null ? undefined : value;
  const safeOptions = (options || []).filter((o) => o.value !== "" && o.value != null);
  return (
    <FieldShell name={name} label={label} hint={hint} required={required} error={error} span={span}>
      <Select value={safeValue} onValueChange={onChange}>
        <SelectTrigger className="h-9 text-sm" data-testid={`field-${name}`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {safeOptions.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  );
}

export function YesNoField(props) {
  return (
    <SelectField
      {...props}
      options={[
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ]}
    />
  );
}

/**
 * Searchable single-select (combobox) — for long option lists such as the
 * ~400 HMRC local-authority codes, where a plain dropdown is unusable.
 * `options` is [{ value, label }]. Type to filter by label.
 */
export function ComboboxField({ name, label, hint, required, error, value, onChange, options, placeholder = "Search…", span }) {
  const [open, setOpen] = useState(false);
  const selected = (options || []).find((o) => o.value === value);
  return (
    <FieldShell name={name} label={label} hint={hint} required={required} error={error} span={span}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-testid={`field-${name}`}
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <span className={selected ? "truncate" : "truncate text-muted-foreground"}>
              {selected ? selected.label : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder={placeholder} data-testid={`field-${name}-search`} />
            <CommandList>
              <CommandEmpty>No match found.</CommandEmpty>
              <CommandGroup>
                {(options || []).map((o) => (
                  <CommandItem
                    key={o.value + o.label}
                    value={o.label}
                    data-testid={`field-${name}-option-${o.value}`}
                    onSelect={() => { onChange(o.value); setOpen(false); }}
                  >
                    <Check className={`mr-2 h-4 w-4 ${o.value === value ? "opacity-100" : "opacity-0"}`} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FieldShell>
  );
}

/**
 * Section wrapper used inside an Accordion item. Provides consistent
 * grid layout (3-col on sm+) for the field primitives.
 */
export function PanelGrid({ children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3 py-3">
      {children}
    </div>
  );
}

/**
 * Small completion badge for the right-edge of each accordion trigger
 * — tells the solicitor at-a-glance which panels still need attention.
 */
export function PanelStatusBadge({ filled, total, hasErrors }) {
  if (total === 0) return null; // informational / notice-only panel
  if (hasErrors) {
    return (
      <span className="ml-auto mr-3 text-[10px] font-bold uppercase tracking-wide bg-rose-100 text-rose-700 px-2 py-0.5 rounded">
        Needs attention
      </span>
    );
  }
  if (filled >= total && total > 0) {
    return (
      <span className="ml-auto mr-3 text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
        Complete
      </span>
    );
  }
  return (
    <span className="ml-auto mr-3 text-[10px] font-semibold text-slate-500">
      {filled} / {total}
    </span>
  );
}
